import { getUTCFromNow, withDateFormat } from '@utils/date-time'
import { TimePickerProps } from 'types/ui/calendar'
import { ComboInput } from '@elements/combo-input'
import { useMemo } from 'react'

export function formatTimePickerLabel(timestamp: number, timezone = 'UTC') {
  return withDateFormat(timestamp, 't', timezone)
}

export function TimePicker({
  value,
  onChange,
  error,
  warning,
  success,
  displayTimezone = 'UTC',
  startOption,
  endOption,
  step = 15 * 60_000,
  disabled = false,
}: TimePickerProps) {
  const timestampSteps = useMemo(() => {
    const parsedStart = Number(startOption)
    const parsedEnd = Number(endOption)
    const parsedStep = Number(step)

    const safeStart = Number.isFinite(parsedStart)
      ? parsedStart
      : getUTCFromNow({ days: 0 })
    const safeEndRaw = Number.isFinite(parsedEnd)
      ? parsedEnd
      : getUTCFromNow({ days: 1 })
    const safeEnd = safeEndRaw >= safeStart ? safeEndRaw : safeStart
    const safeStep =
      Number.isFinite(parsedStep) && parsedStep > 0 ? parsedStep : 15 * 60_000

    // Guard against malformed ranges creating massive option arrays.
    const maxSteps = 7 * 24 * 4
    const timestamps: number[] = []
    for (
      let stamp = safeStart, count = 0;
      stamp <= safeEnd && count < maxSteps;
      stamp += safeStep, count += 1
    ) {
      timestamps.push(stamp)
    }
    return timestamps
  }, [endOption, startOption, step])

  const timeSelectOptions = useMemo(
    () =>
      timestampSteps.map((timestamp) => ({
        id: timestamp,
        name: formatTimePickerLabel(timestamp, displayTimezone),
      })),
    [displayTimezone, timestampSteps]
  )

  return (
    <ComboInput
      type='tel'
      error={error}
      warning={warning}
      success={success}
      disabled={disabled}
      options={timeSelectOptions}
      onChange={(val) => val && onChange?.(val.id)}
      value={
        value
          ? { id: value, name: formatTimePickerLabel(value, displayTimezone) }
          : { id: null, name: '' }
      }
    />
  )
}
